/* eslint-disable @typescript-eslint/no-explicit-any */
import EmptyData from "features/analytics/Components/EmptyData";
import React, { memo, useEffect, useMemo } from "react";
import { Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { IconDispatcher } from "shared/Icons";
import GenericPopover from "shared/popovers/GenericPopover";
import { Button } from "shared/ui-basics";
import styled from "styled-components/macro";
import { Flex } from "../../modals/comment-center.style";
import { setDiscussion, setOpenDiscussion } from "../../reducer/commentSlice";
import { createComment, deleteDiscussion } from "../../reducer/MapActions";
import CommentEditor from "../CommentEditor";
import Discussion from "./discussion";

type Props = {
	discussions: any;
	subject: string;
	className?: string;
};
const popoverParams = {
	width: 432,
	hideArrow: true,
	draggable: true,
	placement: "auto",
	shadow: "none",
	animation: true,
};

const AddCommentPopup = (props: Props): JSX.Element => {
	const innerRef = React.useRef<{ show_popover?: () => void; hide_popover?: () => void; show: boolean }>(null);
	const { t } = useTranslation();
	const { discussions, subject } = props;

	const open = useSelector((state: any) => discussions?.includes(state.ui?.comment?.openDiscussion) || false);
	const uiDiscussions = useSelector((state: any) => state.ui?.comment?.discussion);
	const cjmDiscussions = useSelector((state: any) => state.maps.current_map?.cjmDiscussions);

	const { cjmIdStr, personaIdStr } = getAllMapsParams(useLocation().search);
	const [discussionState, setDiscussionState] = React.useState<any>(discussions || []);
	const [tabKey, setTabKey] = React.useState<string>("open");
	const showComments = useSelector(({ maps }: any) => maps.current_map?.showComments || false);
	const showResolved = useSelector(({ maps }: any) => maps.current_map?.showResolvedComments || false);

	const showComment = () => {
		if (innerRef.current?.show_popover) {
			innerRef.current.show_popover();
		}
	};

	const closeComment = () => {
		if (innerRef.current?.hide_popover) {
			innerRef.current.hide_popover();
		}
	};

	const onSubmit = (value: string, mentions: any) => {
		createComment({
			discussionId: uiDiscussions.id,
			description: value,
			mentions: mentions,
			cjmIdStr,
			personaIdStr,
		});
		if (!discussionState.includes(uiDiscussions.id)) {
			setDiscussionState([...discussionState, uiDiscussions.id]);
		}
		setOpenDiscussion(null);
		closeComment();
		setDiscussion(null);
	};

	const resolvedFilter = (discussionId: any) => cjmDiscussions?.[discussionId]?.isResolved === (tabKey === "resolved");

	const counts = useMemo(() => {
		if (!discussionState || !cjmDiscussions) return { open: 0, resolved: 0 };
		return discussionState?.reduce(
			(acc: any, discussionId: string) => {
				if (!cjmDiscussions[discussionId]) return acc;
				if (cjmDiscussions[discussionId]?.comments?.length === 0) return acc;
				if (cjmDiscussions?.[discussionId]?.isResolved) {
					acc.resolved++;
				} else {
					acc.open++;
				}
				return acc;
			},
			{ open: 0, resolved: 0 }
		);
	}, [discussionState, cjmDiscussions]);

	const custom = (
		<PopoverStyle onClick={(e: any) => e.stopPropagation()}>
			{open && uiDiscussions ? (
				<div className="p-2">
					{/* { this is only when creating a new discussion} */}
					<CommentEditor onSubmit={onSubmit} placeholder="TYPE_A_COMMENT" autoFocus={true} />
				</div>
			) : (
				!uiDiscussions && (
					<>
						<Tabs activeKey={tabKey} onSelect={(k: any) => setTabKey(k)} className="mb-2 nav-justified">
							<Tab eventKey="open" title={`${t("CS_38")} (${counts.open})`} />
							<Tab eventKey="resolved" title={`${t("RESOLVED")} (${counts.resolved})`} />
						</Tabs>
						<Flex vertical gap={8} className="w-100">
							{discussionState?.filter(resolvedFilter)?.map((discussionId: string) => {
								return (
									<Discussion
										key={discussionId}
										discussionId={discussionId}
										discussion={cjmDiscussions?.[discussionId]}
										subject={subject}
										setDiscussionState={setDiscussionState}
										innerRef={innerRef}
									/>
								);
							})}
							{discussionState?.filter(resolvedFilter).length === 0 && (
								<Flex align="center" className="p-4">
									<EmptyData text={t("NO_MESSAGE_HERE")} icon="mailbox" params={{ style: "thin", family: "sharp", size: "md" }} />{" "}
								</Flex>
							)}
						</Flex>
					</>
				)
			)}
		</PopoverStyle>
	);

	const onToggle = () => {
		if (cjmDiscussions?.[uiDiscussions?.id]?.comments?.length === 0) {
			// delete discussion if no comments (only for new discussions)
			deleteDiscussion({
				discussionId: uiDiscussions?.id,
				cjmId: cjmIdStr,
				personaId: personaIdStr,
			});
			setDiscussionState(discussionState?.filter((id: string) => id !== uiDiscussions?.id));
		}
		if(open)
		setOpenDiscussion(null);

		if(uiDiscussions)
		setTimeout(() => {
			setDiscussion(null);
		}, 100);
	};
	const onClick = (e: any) => {
		e.stopPropagation();
		if (innerRef.current?.show) {
			closeComment();
		} else if (innerRef.current?.show_popover) {
			showComment();
		}
	};

	useEffect(() => {
		if (open && innerRef.current) {
			showComment();
		}
		if (!open && discussionState.length === 0 && innerRef.current?.show) {
			closeComment();
		}

	}, [open]);
	useEffect(() => {
		return () => {
			if (cjmDiscussions?.[uiDiscussions?.id]?.comments?.length === 0) {
				// delete discussion if no comments (only for new discussions)
				deleteDiscussion({
					discussionId: uiDiscussions?.id,
					cjmId: cjmIdStr,
					personaId: personaIdStr,
				});
				setDiscussionState(discussionState?.filter((id: string) => id !== uiDiscussions?.id));
			}
			setOpenDiscussion(null);
		}
	}, [])


	if (!showComments) return <></>;
	const totalCounts = showResolved ? counts.open + counts.resolved : counts.open;
	return (
		<div id={"commentPopup_" + discussions?.join("|")}>
			<GenericPopover
				custom={custom}
				innerRef={innerRef}
				params={{
					...popoverParams,
					noOverflow: true,
					shadow: open && uiDiscussions ? " 0px 9px 28px 8px rgba(0, 0, 0, 0.05), 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);" : "none",
				}}
				onToggle={onToggle}
				disabled={false}>
				<>
					{totalCounts > 0 && (
						<CustomButton variant="light-link" className={"mb-2 " + props?.className} size="s" onClick={onClick}>
							<IconDispatcher type="comment" />
							<div className="ms-1">{totalCounts} </div>
						</CustomButton>
					)}
				</>
			</GenericPopover>
		</div>
	);
};

function getAllMapsParams(search: string) {
	const params = new URLSearchParams(search);
	const cjmIdStr = params.get("cjmid");
	const personaIdStr = params.get("personaId");
	return { cjmIdStr, personaIdStr };
}
interface PopoverStyleProps extends React.HTMLAttributes<HTMLDivElement> {
	onClick?: any;
	creationMode?: boolean;
}
const PopoverStyle = styled.div<PopoverStyleProps>`
	/* box-shadow: 0px 8px 16px 0px #00000026; */
	/* border : 1px solid #e5e5e5; */
	padding: 8px;
	border-radius: 4px;
	max-height: 640px;
	overflow-y: auto;
	border-color: #d9d9d9;
	box-shadow: 0px 9px 28px 8px rgba(0, 0, 0, 0.05), 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
	.nav-tabs {
		direction: center;
		border-bottom: 1px solid #f0f0f0;
	}
	.nav-tabs .nav-link {
		color: rgba(0, 0, 0, 0.6) !important;
	}
	.nav-tabs .nav-link:hover {
		color: #333333 !important;
		background-color: #fff !important;
		border-bottom: 1px solid #333333 !important;
	}
	.nav-tabs .nav-link.active {
		color: #333333 !important;
		background-color: #fff !important;
		border-bottom: 1px solid #333333 !important;
		font-weight: 500;
	}
`;
const CustomButton = styled(Button)`
	height: 24px;

	background-color: #f0f0f0 !important;
	padding: 0 0.5rem;
	:hover {
		background-color: #e5e5e5 !important;
	}
`;

export default memo(AddCommentPopup);
